import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Button, ButtonGroup, Checkbox, Icon, Spinner, Text, Banner } from '@shopify/polaris';
import { MagicIcon, LanguageTranslateIcon, AlertTriangleIcon, InfoIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import { diffWords, collapseUnchanged } from './textDiff.js';

/*
  "Revise language": proofreads every text of the quotation (pricing descriptions, technical descriptions, PDF intro
  and terms, group names) or translates all of it, and shows the suggestions as a diff to accept one by one.
  The server runs the AI as a background job (POST start, GET poll) and only writes on Apply.
*/

// The PDF languages (own technical description fields, PDF labels and standard terms) are codes; any other language is
// sent by its English name and translated in place. The first ones are the frequent ones, shown as pills.
const PDF_LANGUAGES = ['E', 'N', 'F'];
const LANGUAGES = [
  { code: 'E', label: 'English' },
  { code: 'N', label: 'Nederlands' },
  { code: 'F', label: 'Français' },
  { code: 'German', label: 'Deutsch' },
  { code: 'Spanish', label: 'Español' },
  { code: 'Italian', label: 'Italiano' },
  { code: 'Portuguese', label: 'Português' },
  { code: 'Polish', label: 'Polski' },
  { code: 'Czech', label: 'Čeština' },
  { code: 'Slovak', label: 'Slovenčina' },
  { code: 'Hungarian', label: 'Magyar' },
  { code: 'Romanian', label: 'Română' },
  { code: 'Bulgarian', label: 'Български' },
  { code: 'Croatian', label: 'Hrvatski' },
  { code: 'Serbian', label: 'Srpski' },
  { code: 'Slovenian', label: 'Slovenščina' },
  { code: 'Greek', label: 'Ελληνικά' },
  { code: 'Danish', label: 'Dansk' },
  { code: 'Swedish', label: 'Svenska' },
  { code: 'Norwegian', label: 'Norsk' },
  { code: 'Finnish', label: 'Suomi' },
  { code: 'Estonian', label: 'Eesti' },
  { code: 'Latvian', label: 'Latviešu' },
  { code: 'Lithuanian', label: 'Lietuvių' },
  { code: 'Ukrainian', label: 'Українська' },
  { code: 'Russian', label: 'Русский' },
  { code: 'Turkish', label: 'Türkçe' },
  { code: 'Arabic', label: 'العربية' },
  { code: 'Hebrew', label: 'עברית' },
  { code: 'Persian', label: 'فارسی' },
  { code: 'Hindi', label: 'हिन्दी' },
  { code: 'Chinese (Simplified)', label: '中文（简体）' },
  { code: 'Chinese (Traditional)', label: '中文（繁體）' },
  { code: 'Japanese', label: '日本語' },
  { code: 'Korean', label: '한국어' },
  { code: 'Vietnamese', label: 'Tiếng Việt' },
  { code: 'Thai', label: 'ไทย' },
  { code: 'Indonesian', label: 'Bahasa Indonesia' },
  { code: 'Malay', label: 'Bahasa Melayu' },
  { code: 'Brazilian Portuguese', label: 'Português (Brasil)' },
  { code: 'Latin American Spanish', label: 'Español (Latinoamérica)' }
];
const FREQUENT_LANGUAGE_COUNT = 6;
const LANGUAGE_LABEL = Object.fromEntries(LANGUAGES.map((language) => [language.code, language.label]));

const GROUPS = [
  { id: 'pricing', label: 'Pricing table' },
  { id: 'technical', label: 'Technical descriptions' },
  { id: 'pdf', label: 'PDF texts' }
];

const ISSUE_STYLES = {
  spelling: { label: 'Spelling', color: '#b42318', bg: '#fee4e2' },
  grammar: { label: 'Grammar', color: '#b54708', bg: '#fef0c7' },
  punctuation: { label: 'Punctuation', color: '#475467', bg: '#eaecf0' },
  clarity: { label: 'Clarity', color: '#175cd3', bg: '#d1e9ff' },
  style: { label: 'Style', color: '#6941c6', bg: '#ebe9fe' },
  terminology: { label: 'Terminology', color: '#0e7090', bg: '#cff9fe' },
  consistency: { label: 'Consistency', color: '#0e7090', bg: '#cff9fe' },
  language: { label: 'Language', color: '#3538cd', bg: '#e0eaff' },
  content: { label: 'Content', color: '#93370d', bg: '#fdead7' }
};

const POLL_MS = 1200;

const STYLES = `
.lr-root { display: flex; flex-direction: column; gap: 16px; }
.lr-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; justify-content: space-between; }
.lr-langs { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.lr-pill { border: 1px solid #d0d5dd; background: #fff; border-radius: 999px; padding: 5px 12px; font-size: 13px; cursor: pointer; color: #344054; transition: all .15s ease; }
.lr-pill:hover:not(:disabled) { border-color: #7a5af8; color: #5925dc; }
.lr-pill.is-active { background: #5925dc; border-color: #5925dc; color: #fff; }
.lr-pill:disabled { opacity: .45; cursor: default; }
.lr-more { appearance: none; -webkit-appearance: none; padding-right: 26px; background-image: linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%); background-position: calc(100% - 13px) 52%, calc(100% - 9px) 52%; background-size: 4px 4px; background-repeat: no-repeat; }
.lr-more.is-active { background-color: #5925dc; }
.lr-more option { color: #101828; background: #fff; }
.lr-progress { padding: 48px 16px; display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center; }
.lr-bar { width: min(360px, 80%); height: 6px; border-radius: 999px; background: #eef0f3; overflow: hidden; }
.lr-bar > div { height: 100%; background: linear-gradient(90deg, #7a5af8, #5925dc); border-radius: 999px; transition: width .4s ease; }
.lr-bar.is-indeterminate > div { width: 35% !important; animation: lr-slide 1.2s ease-in-out infinite; }
@keyframes lr-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
.lr-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; justify-content: space-between; padding: 10px 12px; background: #f9fafb; border: 1px solid #eaecf0; border-radius: 10px; }
.lr-chips { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.lr-chip { border: 1px solid transparent; border-radius: 999px; padding: 2px 10px; font-size: 12px; cursor: pointer; background: #eaecf0; color: #344054; }
.lr-chip.is-active { outline: 2px solid #5925dc; outline-offset: 1px; }
.lr-findings { border: 1px solid #fedf89; background: #fffcf5; border-radius: 10px; padding: 10px 12px; }
.lr-findings-head { display: flex; align-items: center; justify-content: space-between; cursor: pointer; user-select: none; }
.lr-finding { display: flex; gap: 8px; align-items: flex-start; padding: 6px 0; border-top: 1px dashed #fde7b0; font-size: 13px; min-width: 0; }
.lr-finding-body { min-width: 0; }
.lr-finding-body .lr-finding-where { display: block; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lr-finding:first-of-type { border-top: none; }
.lr-finding-where { color: #667085; }
.lr-group-title { display: flex; align-items: center; gap: 8px; margin: 4px 0 8px; }
.lr-count { font-size: 12px; color: #667085; background: #f2f4f7; border-radius: 999px; padding: 0 8px; }
.lr-cards { display: flex; flex-direction: column; gap: 10px; }
.lr-card { display: grid; grid-template-columns: 28px 1fr; gap: 4px 8px; border: 1px solid #eaecf0; border-radius: 10px; padding: 12px 14px 12px 10px; background: #fff; transition: border-color .15s ease, opacity .15s ease, box-shadow .15s ease; }
.lr-card.is-selected { border-color: #bdb4fe; box-shadow: 0 1px 2px rgba(89,37,220,.08); }
.lr-card:not(.is-selected) { opacity: .62; }
.lr-card-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; cursor: pointer; user-select: none; }
.lr-field { font-weight: 600; font-size: 13px; color: #101828; }
.lr-where { font-size: 12px; color: #667085; }
.lr-tag { font-size: 11px; border-radius: 4px; padding: 1px 6px; background: #f2f4f7; color: #475467; }
.lr-tag.is-warn { background: #fef0c7; color: #93370d; }
.lr-issues { display: flex; flex-direction: column; gap: 3px; margin: 6px 0 2px; }
.lr-issue { display: flex; gap: 6px; align-items: baseline; font-size: 12px; color: #475467; }
.lr-issue-type { font-size: 11px; font-weight: 600; border-radius: 4px; padding: 0 6px; white-space: nowrap; }
.lr-diff { margin-top: 6px; font-size: 13.5px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; color: #1d2939; }
.lr-diff del { background: #fee4e2; color: #912018; text-decoration: line-through; text-decoration-color: rgba(145,32,24,.5); border-radius: 3px; padding: 0 1px; }
.lr-diff ins { background: #d1fadf; color: #05603a; text-decoration: none; border-radius: 3px; padding: 0 1px; }
.lr-diff .lr-ellipsis { color: #98a2b3; }
.lr-split { margin-top: 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.lr-split > div { font-size: 13px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; border-radius: 8px; padding: 8px 10px; max-height: 260px; overflow: auto; }
.lr-before { background: #f9fafb; color: #475467; border: 1px solid #eaecf0; }
.lr-after { background: #f6fef9; color: #054f31; border: 1px solid #a6f4c5; }
.lr-split-label { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #98a2b3; margin-bottom: 4px; display: block; }
.lr-inline { margin-top: 6px; display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 10px; font-size: 13.5px; }
.lr-inline .lr-from { color: #667085; }
.lr-inline .lr-arrow { color: #98a2b3; }
.lr-inline .lr-to { color: #05603a; font-weight: 500; }
.lr-actions { display: flex; gap: 12px; margin-left: auto; }
.lr-link { background: none; border: none; padding: 0; font-size: 12px; color: #5925dc; cursor: pointer; }
.lr-link:hover { text-decoration: underline; }
.lr-edit { width: 100%; margin-top: 6px; font: inherit; font-size: 13.5px; line-height: 1.5; border: 1px solid #bdb4fe; border-radius: 8px; padding: 8px 10px; resize: vertical; box-sizing: border-box; }
.lr-empty { padding: 48px 16px; display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
@media (max-width: 720px) { .lr-split { grid-template-columns: 1fr; } }
`;

async function requestJson(url, options) {
  const response = await fetch(url, options);
  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }
  if (!response.ok) throw new Error(body?.message || `Request failed (${response.status})`);
  return body;
}

function DiffView({ before, after, expanded }) {
  const ops = useMemo(() => {
    const diff = diffWords(before, after);
    return expanded ? diff : collapseUnchanged(diff);
  }, [before, after, expanded]);
  return (
    <div className="lr-diff">
      {ops.map((op, index) =>
        op.type === 'del' ? (
          <del key={index}>{op.text}</del>
        ) : op.type === 'ins' ? (
          <ins key={index}>{op.text}</ins>
        ) : op.collapsed ? (
          <span key={index} className="lr-ellipsis">
            {op.text}
          </span>
        ) : (
          <span key={index}>{op.text}</span>
        )
      )}
    </div>
  );
}

function ChangeCard({ change, mode, selected, onToggle, editedText, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const afterText = editedText ?? change.afterText;
  const canEdit = change.format === 'plain';
  const isLong = change.beforeText.length + afterText.length > 500;
  const isShort = change.beforeText.length <= 90 && afterText.length <= 90 && !/\n/.test(change.beforeText + afterText);

  return (
    <div className={`lr-card${selected ? ' is-selected' : ''}`}>
      <div style={{ paddingTop: '1px' }}>
        <Checkbox label="Accept this change" labelHidden checked={selected} onChange={onToggle} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="lr-card-head" onClick={onToggle}>
          <span className="lr-field">{change.fieldLabel}</span>
          <span className="lr-where">{change.label}</span>
          {change.replacesExisting && <span className="lr-tag is-warn">Replaces the existing {LANGUAGE_LABEL[change.language] || ''} text</span>}
          {change.cannedDefault && <span className="lr-tag">Standard text</span>}
          {editedText != null && <span className="lr-tag">Edited</span>}
          <div className="lr-actions" onClick={(event) => event.stopPropagation()}>
            {canEdit && (
              <button type="button" className="lr-link" onClick={() => setEditing((value) => !value)}>
                {editing ? 'Done editing' : 'Edit'}
              </button>
            )}
            {canEdit && editedText != null && !editing && (
              <button type="button" className="lr-link" onClick={() => onEdit(null)}>
                Undo edit
              </button>
            )}
            {mode === 'review' && isLong && !editing && (
              <button type="button" className="lr-link" onClick={() => setExpanded((value) => !value)}>
                {expanded ? 'Changes only' : 'Full text'}
              </button>
            )}
          </div>
        </div>

        {change.issues?.length > 0 && (
          <div className="lr-issues">
            {change.issues.map((issue, index) => {
              const style = ISSUE_STYLES[issue.type] || ISSUE_STYLES.style;
              return (
                <div key={index} className="lr-issue">
                  <span className="lr-issue-type" style={{ color: style.color, background: style.bg }}>
                    {style.label}
                  </span>
                  <span>{issue.message}</span>
                </div>
              );
            })}
          </div>
        )}

        {editing ? (
          <textarea className="lr-edit" rows={Math.min(8, Math.max(2, Math.ceil(afterText.length / 90)))} value={afterText} onChange={(event) => onEdit(event.target.value)} autoFocus />
        ) : mode === 'translate' && isShort ? (
          <div className="lr-inline">
            <span className="lr-from">{change.beforeText || '(empty)'}</span>
            <span className="lr-arrow">→</span>
            <span className="lr-to">{afterText}</span>
          </div>
        ) : mode === 'translate' ? (
          <div className="lr-split">
            <div className="lr-before">
              <span className="lr-split-label">{change.before ? 'Now' : 'Now (empty)'}</span>
              {change.beforeText || '—'}
            </div>
            <div className="lr-after">
              <span className="lr-split-label">Translation</span>
              {afterText}
            </div>
          </div>
        ) : (
          <DiffView before={change.beforeText} after={afterText} expanded={expanded} />
        )}
      </div>
    </div>
  );
}

const FINDINGS_PREVIEW = 3;

function Findings({ findings }) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  if (!findings.length) return null;
  const shown = showAll ? findings : findings.slice(0, FINDINGS_PREVIEW);
  const warnings = findings.filter((finding) => finding.severity === 'warning').length;
  return (
    <div className="lr-findings">
      <div className="lr-findings-head" onClick={() => setOpen((value) => !value)}>
        <Text as="span" variant="headingSm">
          {`Worth a look (${findings.length})`}
        </Text>
        <span className="lr-where">
          {warnings ? `${warnings} warning${warnings === 1 ? '' : 's'} · ` : ''}
          {open ? 'Hide' : 'Show'}
        </span>
      </div>
      {open && (
        <div style={{ marginTop: '6px' }}>
          {shown.map((finding, index) => (
            <div key={index} className="lr-finding">
              <span style={{ flex: '0 0 auto', marginTop: '1px' }}>
                <Icon source={finding.severity === 'warning' ? AlertTriangleIcon : InfoIcon} tone={finding.severity === 'warning' ? 'warning' : 'subdued'} />
              </span>
              <span className="lr-finding-body">
                {finding.message}
                {finding.kind === 'pricing' ? (
                  <span className="lr-tag" style={{ marginLeft: '6px' }}>
                    Pricing
                  </span>
                ) : null}
                {finding.where ? (
                  <span className="lr-finding-where" title={finding.where}>
                    {finding.where}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
          {findings.length > FINDINGS_PREVIEW && (
            <button type="button" className="lr-link" style={{ marginTop: '4px' }} onClick={() => setShowAll((value) => !value)}>
              {showAll ? 'Show fewer' : `Show all ${findings.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LanguageRevisionModal({ open, onClose, quotationId, pdfLanguage = 'E', onApplied }) {
  const [mode, setMode] = useState('review');
  const [targetLanguage, setTargetLanguage] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | running | results | applying | applied | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [issueFilter, setIssueFilter] = useState('all');
  const [switchPdfLanguage, setSwitchPdfLanguage] = useState(true);
  const [applyOutcome, setApplyOutcome] = useState(null);
  const runRef = useRef(0);
  const cacheRef = useRef(new Map());

  const showResult = useCallback((data) => {
    setResult(data);
    setSelected(new Set(data.changes.filter((change) => change.defaultSelected !== false).map((change) => change.id)));
    setEdits({});
    setIssueFilter('all');
    setPhase('results');
  }, []);

  const start = useCallback(
    async (nextMode, nextTarget = null) => {
      const cacheKey = nextMode === 'translate' ? `translate:${nextTarget}` : 'review';
      setMode(nextMode);
      setTargetLanguage(nextTarget);
      setError(null);
      setApplyOutcome(null);
      if (nextMode === 'translate' && !nextTarget) {
        runRef.current += 1;
        setResult(null);
        setPhase('idle');
        return;
      }
      if (cacheRef.current.has(cacheKey)) {
        runRef.current += 1;
        showResult(cacheRef.current.get(cacheKey));
        return;
      }

      const runId = ++runRef.current;
      setPhase('running');
      setResult(null);
      setProgress({ done: 0, total: 0 });
      try {
        let job = await requestJson(`/api/project-quotations/${quotationId}/language-revisions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: nextMode, targetLanguage: nextTarget })
        });
        while (job.status === 'running') {
          await new Promise((resolve) => setTimeout(resolve, POLL_MS));
          if (runRef.current !== runId) return;
          job = await requestJson(`/api/project-quotations/${quotationId}/language-revisions/${job.jobId}?_ts=${Date.now()}`);
          if (runRef.current !== runId) return;
          setProgress(job.progress || { done: 0, total: 0 });
        }
        if (runRef.current !== runId) return;
        if (job.status === 'error') throw new Error(job.error || 'The AI request failed');
        cacheRef.current.set(cacheKey, job.result);
        showResult(job.result);
      } catch (err) {
        if (runRef.current !== runId) return;
        setError(err.message);
        setPhase('error');
      }
    },
    [quotationId, showResult]
  );

  // Proofreading starts as soon as the modal opens: zero clicks to the first useful answer
  useEffect(() => {
    if (!open) return undefined;
    cacheRef.current = new Map();
    setSwitchPdfLanguage(true);
    start('review');
    return () => {
      runRef.current += 1;
    };
  }, [open, quotationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const changes = result?.changes || [];
  const visibleChanges = useMemo(() => (issueFilter === 'all' ? changes : changes.filter((change) => change.issues?.some((issue) => issue.type === issueFilter))), [changes, issueFilter]);
  const issueCounts = useMemo(() => {
    const counts = {};
    for (const change of changes) {
      for (const type of new Set((change.issues || []).map((issue) => issue.type))) counts[type] = (counts[type] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [changes]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected = visibleChanges.length > 0 && visibleChanges.every((change) => selected.has(change.id));
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const change of visibleChanges) {
        if (allVisibleSelected) next.delete(change.id);
        else next.add(change.id);
      }
      return next;
    });

  const selectedCount = changes.filter((change) => selected.has(change.id)).length;
  const moreLanguageSelected = LANGUAGES.slice(FREQUENT_LANGUAGE_COUNT).some((language) => language.code === targetLanguage);
  const isPdfLanguage = PDF_LANGUAGES.includes(targetLanguage);
  const willSwitchLanguage = mode === 'translate' && isPdfLanguage && targetLanguage !== pdfLanguage && switchPdfLanguage;

  const apply = async () => {
    const payload = changes
      .filter((change) => selected.has(change.id))
      .map((change) => ({ id: change.id, target: change.target, before: change.before, after: edits[change.id] != null ? edits[change.id] : change.after }));
    if (!payload.length && !willSwitchLanguage) return;
    setPhase('applying');
    try {
      const outcome = await requestJson(`/api/project-quotations/${quotationId}/language-revisions/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes: payload, pdfLanguage: willSwitchLanguage ? targetLanguage : null })
      });
      setApplyOutcome({ ...outcome, switchedLanguage: willSwitchLanguage ? targetLanguage : null });
      cacheRef.current = new Map(); // the texts changed: earlier suggestions are stale
      setPhase('applied');
      if (onApplied) onApplied();
    } catch (err) {
      setError(err.message);
      setPhase('results');
    }
  };

  const grouped = GROUPS.map((group) => ({ ...group, changes: visibleChanges.filter((change) => change.group === group.id) })).filter((group) => group.changes.length);

  const primaryAction =
    phase === 'applied'
      ? { content: 'Done', onAction: onClose }
      : phase === 'results' || phase === 'applying'
      ? {
          content: selectedCount
            ? `Apply ${selectedCount} change${selectedCount === 1 ? '' : 's'}${willSwitchLanguage ? ` & switch PDF to ${LANGUAGE_LABEL[targetLanguage]}` : ''}`
            : willSwitchLanguage
            ? `Switch PDF to ${LANGUAGE_LABEL[targetLanguage]}`
            : 'Apply changes',
          onAction: apply,
          loading: phase === 'applying',
          disabled: !selectedCount && !willSwitchLanguage
        }
      : undefined;

  return (
    <Modal open={open} onClose={onClose} title="Revise language" large primaryAction={primaryAction} secondaryActions={phase === 'applied' ? [] : [{ content: 'Close', onAction: onClose }]}>
      <style>{`${STYLES}
        .Polaris-Modal-Dialog__Modal { max-width: 1040px !important; width: min(1040px, 94vw) !important; }`}</style>
      <Modal.Section>
        <div className="lr-root">
          {phase !== 'applied' && (
            <div className="lr-toolbar">
              <ButtonGroup variant="segmented">
                <Button pressed={mode === 'review'} icon={MagicIcon} onClick={() => mode !== 'review' && start('review')}>
                  Proofread
                </Button>
                <Button pressed={mode === 'translate'} icon={LanguageTranslateIcon} onClick={() => mode !== 'translate' && start('translate', null)}>
                  Translate
                </Button>
              </ButtonGroup>
              {mode === 'translate' ? (
                <div className="lr-langs">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Translate everything to
                  </Text>
                  {LANGUAGES.slice(0, FREQUENT_LANGUAGE_COUNT).map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      className={`lr-pill${targetLanguage === language.code ? ' is-active' : ''}`}
                      disabled={phase === 'running' || phase === 'applying'}
                      onClick={() => targetLanguage !== language.code && start('translate', language.code)}
                    >
                      {language.label}
                    </button>
                  ))}
                  <select
                    aria-label="More languages"
                    className={`lr-pill lr-more${moreLanguageSelected ? ' is-active' : ''}`}
                    disabled={phase === 'running' || phase === 'applying'}
                    value={moreLanguageSelected ? targetLanguage : ''}
                    onChange={(event) => event.target.value && event.target.value !== targetLanguage && start('translate', event.target.value)}
                  >
                    <option value="">More languages…</option>
                    {LANGUAGES.slice(FREQUENT_LANGUAGE_COUNT).map((language) => (
                      <option key={language.code} value={language.code}>
                        {language.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <Text as="span" variant="bodySm" tone="subdued">
                  {`Spelling, grammar, wording and consistency of every text · PDF language: ${LANGUAGE_LABEL[pdfLanguage] || pdfLanguage}`}
                </Text>
              )}
            </div>
          )}

          {error && phase !== 'error' && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {phase === 'idle' && mode === 'translate' && (
            <div className="lr-empty">
              <Icon source={LanguageTranslateIcon} tone="subdued" />
              <Text as="p" variant="headingSm">
                Pick a language
              </Text>
              <Text as="p" tone="subdued">
                Descriptions, technical descriptions, the PDF introduction, terms and group names are translated in one go. You review everything before it is saved.
              </Text>
            </div>
          )}

          {phase === 'running' && (
            <div className="lr-progress">
              <Spinner size="small" />
              <Text as="p" variant="headingSm">
                {mode === 'translate' ? `Translating to ${LANGUAGE_LABEL[targetLanguage]}…` : 'Reading every text in the quotation…'}
              </Text>
              <div className={`lr-bar${progress.total ? '' : ' is-indeterminate'}`}>
                <div style={{ width: progress.total ? `${Math.max(6, (progress.done / progress.total) * 100)}%` : '35%' }} />
              </div>
              <Text as="p" variant="bodySm" tone="subdued">
                {progress.total > 1 ? `Part ${Math.min(progress.done + 1, progress.total)} of ${progress.total} · usually takes 10–40 seconds` : 'Usually takes 10–40 seconds'}
              </Text>
            </div>
          )}

          {phase === 'error' && (
            <Banner tone="critical" title="The revision could not be completed" action={{ content: 'Try again', onAction: () => start(mode, targetLanguage) }}>
              {error}
            </Banner>
          )}

          {(phase === 'results' || phase === 'applying') && result && (
            <>
              {mode === 'translate' && !isPdfLanguage && changes.length > 0 && (
                <Banner tone="info">{`The texts are translated in place. The PDF's fixed labels (headings, table columns, totals) stay in ${LANGUAGE_LABEL[pdfLanguage] || pdfLanguage}.`}</Banner>
              )}
              {result.warnings?.length > 0 && (
                <Banner tone="warning">
                  {result.warnings.map((warning, index) => (
                    <p key={index}>{warning}</p>
                  ))}
                </Banner>
              )}

              {mode === 'review' && <Findings findings={result.findings || []} />}

              {changes.length === 0 ? (
                <div className="lr-empty">
                  <Icon source={CheckCircleIcon} tone="success" />
                  <Text as="p" variant="headingSm">
                    {mode === 'translate' ? `Everything is already in ${LANGUAGE_LABEL[targetLanguage]}` : 'No language issues found'}
                  </Text>
                  <Text as="p" tone="subdued">
                    {`Checked ${result.stats?.fields || 0} texts.`}
                  </Text>
                </div>
              ) : (
                <>
                  <div className="lr-summary">
                    <div className="lr-chips">
                      <Text as="span" variant="headingSm">
                        {mode === 'translate' ? `${changes.length} text${changes.length === 1 ? '' : 's'} to translate` : `${changes.length} suggestion${changes.length === 1 ? '' : 's'}`}
                      </Text>
                      {mode === 'review' && issueCounts.length > 1 && (
                        <>
                          <button type="button" className={`lr-chip${issueFilter === 'all' ? ' is-active' : ''}`} onClick={() => setIssueFilter('all')}>
                            {`All ${changes.length}`}
                          </button>
                          {issueCounts.map(([type, count]) => {
                            const style = ISSUE_STYLES[type] || ISSUE_STYLES.style;
                            return (
                              <button
                                key={type}
                                type="button"
                                className={`lr-chip${issueFilter === type ? ' is-active' : ''}`}
                                style={{ background: style.bg, color: style.color }}
                                onClick={() => setIssueFilter(issueFilter === type ? 'all' : type)}
                              >
                                {`${style.label} ${count}`}
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                    <div className="lr-chips">
                      {mode === 'translate' && isPdfLanguage && targetLanguage !== pdfLanguage && (
                        <Checkbox label={`Switch the PDF to ${LANGUAGE_LABEL[targetLanguage]}`} checked={switchPdfLanguage} onChange={setSwitchPdfLanguage} />
                      )}
                      <Button variant="plain" onClick={toggleAllVisible}>
                        {allVisibleSelected ? 'Deselect all' : 'Select all'}
                      </Button>
                    </div>
                  </div>

                  {grouped.map((group) => (
                    <div key={group.id}>
                      <div className="lr-group-title">
                        <Text as="h3" variant="headingSm">
                          {group.label}
                        </Text>
                        <span className="lr-count">{group.changes.length}</span>
                      </div>
                      <div className="lr-cards">
                        {group.changes.map((change) => (
                          <ChangeCard
                            key={change.id}
                            change={change}
                            mode={mode}
                            selected={selected.has(change.id)}
                            onToggle={() => toggle(change.id)}
                            editedText={edits[change.id]}
                            onEdit={(text) =>
                              setEdits((prev) => {
                                const next = { ...prev };
                                if (text == null) delete next[change.id];
                                else next[change.id] = text;
                                return next;
                              })
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {phase === 'applied' && applyOutcome && (
            <div className="lr-empty">
              <Icon source={CheckCircleIcon} tone="success" />
              <Text as="p" variant="headingMd">
                {`${applyOutcome.applied.length} change${applyOutcome.applied.length === 1 ? '' : 's'} applied`}
              </Text>
              {applyOutcome.switchedLanguage && (
                <Text as="p" tone="subdued">
                  {`The PDF is now in ${LANGUAGE_LABEL[applyOutcome.switchedLanguage]}.`}
                </Text>
              )}
              {applyOutcome.conflicts.length > 0 && (
                <Text as="p" tone="caution">
                  {`${applyOutcome.conflicts.length} text${applyOutcome.conflicts.length === 1 ? ' was' : 's were'} edited in the meantime and left untouched.`}
                </Text>
              )}
              <div style={{ marginTop: '8px' }}>
                <Button onClick={() => start('review')}>Proofread again</Button>
              </div>
            </div>
          )}
        </div>
      </Modal.Section>
    </Modal>
  );
}
